/** Home dashboard: summary line, three status tiles, the month pipeline, two
    co-equal activity/decision regions, and the point-in-time engagement band. */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties, FocusEvent, ReactNode } from "react";
import { Loading, ErrorState } from "../components/ViewStates";
import {
  useUniverse,
  useSignals,
  useAgentRuns,
  useCalendar,
  useBrandPlannable,
  useFactProposals,
  useSocialStats,
  useConnectedAccounts,
  useBrandIdentity,
  usePendingSignalActions,
  signalEvidenceStats,
  humanizeAgentType,
  type AgentLiveness,
  type MonthLiveStatus,
  type MonthPipelineRow,
} from "../data/useCreativeOsHome";
import type { StageFact } from "../data/universeLayout";
import { computeUniverseMaturity, maturityLabel } from "../data/universeLayout";
import type { MarketSignal } from "@/types/marketSignal.type";
import type { SocialAccountStats, SocialProvider } from "../settings/api/useSocialConnections";
import { usePermissions } from "@/hooks/usePermissions";
import { kindLabelSingular, approxDuration } from "@/app/routes/dashboard/market-signals/components/signalMeta";

const E = "cubic-bezier(0.16,1,0.3,1)";

/** A decision taken in place, and only what the endpoint behind it actually does.
    Accepting a pending signal does ONE thing: AcceptSignalUseCase sets its status
    to ACTIVE with a lifetime. It writes no fact. Turning a signal into a
    BrandUniverseFact is a separate call (GraduateSignalUseCase), and the
    BrandUniverseChatFactProposal queue is a third, unrelated thing. Copy on these
    rows must never promise any of the other two.

    There are TWO decisions, because there are two endpoints. "Open in Universe"
    is a plain navigation and was never a third: it recorded nothing, so it is
    not a decision, gets no confirmation and offers no Undo. */
type Decision = "accepted" | "declined";
const DECIDED_COPY: Record<Decision, string> = {
  accepted: "Accepted",
  declined: "Declined",
};

/** Hover highlight link between a fact and its grounded signal/call. */
type Seam = { factId: string | null; callId: string | null };
const NO_SEAM: Seam = { factId: null, callId: null };

/** Relative time from an ISO stamp ("6m ago"). Mirrors the shell's `ago`. */
function ago(iso: string | null): string {
  if (!iso) return "just now";
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Short calendar date ("14 Jul"). */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * The heartbeat sentence, plus the fact it is grounded in. `everRan` is exactly
 * "this brand has at least one agent run on record", and it is what separates
 * NEVER RAN from QUIET: only a brand whose agents have actually run may be
 * described as watching anything.
 */
type Heartbeat = { text: string; everRan: boolean };

/** Curated agent-liveness heartbeat: one sentence, never a raw run log. */
function agentHeartbeat(a: AgentLiveness): Heartbeat | null {
  const everRan = a.runs.length > 0;
  const parts: string[] = [];
  const last = a.finishedRecent[0];
  if (last?.finishedAt) parts.push(`${humanizeAgentType(last.agentType)} finished ${ago(last.finishedAt)}`);
  const run = a.running[0];
  if (run) parts.push(`${humanizeAgentType(run.agentType)} running now`);
  else if (a.queued.length) parts.push(`${a.queued.length} ${a.queued.length === 1 ? "run" : "runs"} queued`);
  if (!parts.length) {
    if (a.lastFinishedAt) return { text: `Agents idle, last ran ${ago(a.lastFinishedAt)}. All healthy.`, everRan };
    /* Nothing running, nothing queued, nothing that ever finished, and no run of
       any kind on record: the agents have LITERALLY never run, and this is the
       one agent statement left on the page. Going silent here is what let the
       copy below claim the agents were watching a brand they had never touched. */
    if (!everRan) return { text: "Your agents have not run yet.", everRan };
    /* Runs exist, but none of them is in a state this sentence can report. There
       is nothing honest to say, so nothing is said. */
    return null;
  }
  parts.push(a.allHealthy ? "all agents healthy" : "one agent needs a look");
  return { text: `${parts.join(", ")}.`, everRan };
}

// ------------------------------------------------------------- how it's landing

/** The five real providers, spelled the way the platforms spell themselves. */
const PROVIDER_LABEL: Record<SocialProvider, string> = {
  FACEBOOK: "Facebook",
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  YOUTUBE: "YouTube",
  SNAPCHAT: "Snapchat",
};

/** One connected account joined to its stats row (stats may be absent). */
type LandingRow = {
  accountId: string;
  provider: SocialProvider;
  /** `accounts[].username`, else the account name. "" when the account carries
      neither, in which case the cell stays empty rather than inventing a handle. */
  handle: string;
  needsReauth: boolean;
  stats: SocialAccountStats | null;
};

const numberOr = (n: number | null): string | null => (n == null ? null : n.toLocaleString());

// ---------------------------------------------------------------- preview shape

/** Shape the dev harness supplies in place of the live hooks. */
export type HomePreview = {
  universe?: ReturnType<typeof useUniverse>;
  signals?: ReturnType<typeof useSignals>;
  agents?: ReturnType<typeof useAgentRuns>;
  brand?: ReturnType<typeof useBrandIdentity>;
  calendar?: ReturnType<typeof useCalendar>;
  plannable?: ReturnType<typeof useBrandPlannable>;
  factProposals?: ReturnType<typeof useFactProposals>;
  socialStats?: ReturnType<typeof useSocialStats>;
  /**
   * The connection read, which is a DIFFERENT query from the stats read.
   * `SocialAccountStats` has no handle and no status, so an account's handle
   * (`accounts[].username`) and its `NEEDS_REAUTH` state exist only here.
   */
  socialConnections?: ReturnType<typeof useConnectedAccounts>;
  actions?: ReturnType<typeof usePendingSignalActions>;
  /** The harness has no signed-in user, so permissions must be supplied or the
      decide-in-place actions (the whole point of Region B) never render. */
  canEdit?: boolean;
};

export default function VantageLineView({
  onOpenSignals,
  onOpenUniverse,
  onOpenStrategy,
  onOpenCalendar,
  preview,
}: {
  onOpenSignals?: () => void;
  onOpenUniverse?: () => void;
  onOpenStrategy?: () => void;
  onOpenCalendar?: () => void;
  /** Dev-only fixture override for the design harness (/design/home-preview).
      Live mounts never pass it, so production reads only the real hooks. */
  preview?: HomePreview;
}) {
  const liveUniverse = useUniverse();
  const liveSignals = useSignals();
  const liveAgents = useAgentRuns();
  const liveBrand = useBrandIdentity();
  const liveCalendar = useCalendar();
  const livePlannable = useBrandPlannable();
  const liveProposals = useFactProposals();
  const liveSocialStats = useSocialStats();
  const liveConnections = useConnectedAccounts();
  const liveActions = usePendingSignalActions();

  /* `rings` is what the maturity read needs: computeUniverseMaturity(facts,
     rings) counts concept MEMBERSHIP, so facts alone report every orbit empty,
     and it returns `offRing` for the facts no orbit owns. */
  const { graph, facts, rings, subjects, total, newCount, isLoading, isError, refetch } =
    preview?.universe ?? liveUniverse;
  const { pendingSignals, activeSignals, isLoading: signalsLoading, signalsError } =
    preview?.signals ?? liveSignals;
  const agents = preview?.agents ?? liveAgents;
  const brand = preview?.brand ?? liveBrand;
  const calendar = preview?.calendar ?? liveCalendar;
  const plannable = preview?.plannable ?? livePlannable;
  const factProposals = preview?.factProposals ?? liveProposals;
  const socialStats = preview?.socialStats ?? liveSocialStats;
  const socialConnections = preview?.socialConnections ?? liveConnections;
  const signalActions = preview?.actions ?? liveActions;

  // Hover highlight: linked fact id + call id.
  const [seam, setSeam] = useState<Seam>(NO_SEAM);

  /* Local decision state; written only on mutation success. Both decisions hit
     the server and neither has a reversal endpoint, so nothing here is undoable
     and no Undo is offered. */
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  // In-flight guard, keyed by call id. Disables the row + blocks double-submits
  // while its mutation runs, cleared on success or error.
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});

  // "Since last visit" from localStorage; first visit defaults to "this week".
  const [sinceLabel, setSinceLabel] = useState<string>("this week");
  // Read last-seen stamp during render (before the write effect below runs).
  const [sinceBrand, setSinceBrand] = useState<string | undefined>(undefined);
  const sinceBrandId = graph.nodes[0]?.brandId;
  if (facts.length && sinceBrandId && sinceBrandId !== sinceBrand) {
    setSinceBrand(sinceBrandId);
    const last = localStorage.getItem(`co:universe:lastSeen:${sinceBrandId}`);
    if (last) {
      const d = new Date(last);
      if (!Number.isNaN(d.getTime())) setSinceLabel(d.toLocaleDateString("en-US", { weekday: "long" }));
    }
  }
  const seenDoneRef = useRef<string | null>(null);
  useEffect(() => {
    const brandId = graph.nodes[0]?.brandId;
    if (!facts.length || !brandId || seenDoneRef.current === brandId) return;
    seenDoneRef.current = brandId;
    localStorage.setItem(`co:universe:lastSeen:${brandId}`, new Date().toISOString());
  }, [graph, facts]);

  const maturity = useMemo(() => computeUniverseMaturity(facts, rings, subjects), [facts, rings, subjects]);

  /* One row per connected ACCOUNT, driven by the connection read rather than the
     stats read: the handle and the NEEDS_REAUTH explanation live only there, and
     an account with no stats row is a real state ("not synced yet"), not a gap. */
  const landingRows = useMemo<LandingRow[]>(() => {
    const statsByAccount = new Map(socialStats.stats.map((s) => [s.accountId, s] as const));
    return socialConnections.connections.flatMap((conn) =>
      conn.accounts.map((account) => ({
        accountId: account.id,
        provider: conn.provider,
        handle: account.username ?? account.name ?? "",
        needsReauth: conn.status === "NEEDS_REAUTH",
        stats: statsByAccount.get(account.id) ?? null,
      })),
    );
  }, [socialConnections.connections, socialStats.stats]);

  if (isLoading) return <Loading label="Reading your OS" minHeight={560} />;
  if (isError) return <ErrorState onRetry={refetch} minHeight={560} />;

  const firstEver = total === 0;

  const decisionRows = buildDecisionRows({
    calendar,
    plannable,
    factProposals,
    onOpenCalendar,
    onOpenStrategy,
    onOpenUniverse,
  });

  // Pending signals sorted by confidence (client-side).
  const ranked = [...pendingSignals].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
  /* Region balance, measured rather than assumed. Both regions are one grid row,
     so the shorter one leaves a visible void under it. With two or more queues
     already stacked above the cards, a fourth card runs Region B roughly 145px
     past Region A's last line; three cards plus the "more waiting in Signals"
     link keeps the two columns within about 70px of each other, and the region
     still shows MORE than it did before (three queues plus three calls, against
     four calls and nothing else).

     Counted off SETTLED rows only. A skeleton is not a queue and an error is
     not a queue, so counting them meant first paint (three skeletons) forced
     three cards, and the branch flipped to four the moment one real row
     answered, a card appearing under whoever was already reading. */
  const shownSignals = ranked.slice(0, decisionRows.settled >= 2 ? 3 : 4);
  const morePending = ranked.length - shownSignals.length;

  const byId = new Map(facts.map((f) => [f.id, f] as const));
  const groundFact = (signal: MarketSignal): StageFact | undefined => {
    for (const id of signal.groundingFactIds) {
      const f = byId.get(id);
      if (f) return f;
    }
    return undefined;
  };
  // factId → first visible call grounded on it (top 4 signals only).
  const callByFactId = new Map<string, string>();
  for (const s of shownSignals) for (const fid of s.groundingFactIds) if (!callByFactId.has(fid)) callByFactId.set(fid, s.id);

  const freshFacts = facts.filter((f) => f.kind === "new");
  const openSlots = [...calendar.slots]
    .filter((s) => s.status === "PLANNED")
    .sort((a, b) => (a.scheduledAt < b.scheduledAt ? -1 : 1));
  /* A slot counts as filled once it has an accepted idea, which is true at
     SIGNED_OFF and at every stage past it. Counting SIGNED_OFF alone left
     IN_CLIENT_REVIEW and CLIENT_APPROVED slots, which are FURTHER along, as
     neither filled nor open against a denominator of every slot, so the month
     read emptier than it actually was. */
  const filledSlots = calendar.slots.filter(
    (s) => s.status === "SIGNED_OFF" || s.status === "IN_CLIENT_REVIEW" || s.status === "CLIENT_APPROVED",
  ).length;
  /* MISSED is the one calendar state that genuinely needs a person, and it was
     invisible on the home. Surfaced beside coverage. */
  const missedSlots = calendar.slots.filter((s) => s.status === "MISSED").length;
  /* Posts with at least one publication that reached PUBLISHED. Sayable for the
     CURRENT month and for no other, because the current month is the only one
     whose slots were fetched; the month read carries platform / status /
     scheduledAt / permalink, so "published" is the whole claim available (there
     is no publish timestamp anywhere in it). */
  const publishedSlots = calendar.slots.filter((s) =>
    (s.deliverable?.publications ?? []).some((p) => p.status === "PUBLISHED"),
  ).length;
  const callsWaiting = pendingSignals.length;
  const ranAgents = agents.finishedRecent.length;

  // Summary sentence; terse when nothing new and nothing pending.
  const quiet = newCount === 0 && callsWaiting === 0;
  let standingLine: string;
  if (firstEver) {
    standingLine = "Your brand is just being born. Connect your sources and your first read starts here.";
  } else if (signalsError) {
    // Partial load: report universe data only, flag the failed query below.
    const grew = newCount > 0 ? `Since ${sinceLabel}: ${newCount} ${newCount === 1 ? "fact" : "facts"} grew. ` : "";
    standingLine = `${grew}Some of your read could not load just now, see the panels below.`;
  } else if (quiet) {
    standingLine = "Your universe is holding steady, nothing waiting.";
  } else {
    const state: string[] = [];
    if (newCount > 0) state.push(`${newCount} ${newCount === 1 ? "fact" : "facts"} grew`);
    if (ranAgents > 0) state.push(`${ranAgents} ${ranAgents === 1 ? "agent" : "agents"} ran`);
    if (openSlots.length > 0) state.push(`${openSlots.length} ${openSlots.length === 1 ? "slot" : "slots"} still open`);
    const head = `Since ${sinceLabel}: ${state.join(", ")}`;
    if (callsWaiting > 0) {
      const tail = `${callsWaiting} ${callsWaiting === 1 ? "call" : "calls"} now waiting on you`;
      standingLine = state.length ? `${head}, and ${tail}.` : `Since ${sinceLabel}: ${tail}.`;
    } else {
      standingLine = `${head}, nothing waiting on you.`;
    }
  }

  const heartbeat = agentHeartbeat(agents);
  /* One agent run on record is the licence for every "your agents are watching"
     line on this page. Without it the page may report the absence and nothing
     more. */
  const agentsEverRan = agents.runs.length > 0;

  const commit = (id: string, d: Decision) => setDecisions((prev) => ({ ...prev, [id]: d }));
  const setBusy = (id: string, v: boolean) => setSubmitting((prev) => ({ ...prev, [id]: v }));

  const decideSignal = (signal: MarketSignal, d: Decision) => {
    if (submitting[signal.id]) return; // double-submit guard
    setBusy(signal.id, true);
    const cbs = {
      onSuccess: () => { commit(signal.id, d); setBusy(signal.id, false); },
      onError: () => setBusy(signal.id, false), // stay actionable; the row was never marked done
    };
    if (d === "accepted") signalActions.accept(signal, cbs);
    else signalActions.dismiss(signal.id, cbs);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", animation: `coRiseT .7s ${E} .05s both` }}>
      <VantageStyle />

      <header style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--co-faint)" }}>
          YOUR BRAND'S READ
        </div>
        {/* No date here. TopBar prints the weekday and date about 30px above
            this line, and the standing line below already carries the time
            framing ("Since Sunday: ..."), so the date appears once on screen. */}
        <h1 style={{ fontFamily: "var(--font-section)", fontWeight: 800, fontSize: "clamp(22px,2.3vw,32px)", lineHeight: 1.08, letterSpacing: "-.012em", color: "var(--co-text)", margin: "10px 0 0" }}>
          {brand.name}.
        </h1>
      </header>

      <div style={{ marginTop: 26, paddingBottom: 30, borderBottom: "1px solid var(--co-line)" }}>
        <p style={{ margin: 0, fontFamily: "var(--font-section)", fontWeight: 650, fontSize: "clamp(17px,1.7vw,23px)", lineHeight: 1.28, letterSpacing: "-.008em", color: "var(--co-text)", maxWidth: 900 }}>
          {standingLine}
        </p>
      </div>

      <section className="co-vl-ps" aria-labelledby="co-vl-ps-label" style={{ marginTop: 34, paddingBottom: 34, borderBottom: "1px solid var(--co-line)" }}>
        {/* h2 styled as mono eyebrow; resets avoid global h2 rules leaking in. */}
        <h2 id="co-vl-ps-label" style={BAND_EYEBROW}>
          PRODUCT STATE
        </h2>
        <div className="co-vl-band" style={{ marginTop: 16 }}>
          {/* Universe: page-gated on load/error above, so it only reads here. */}
          <StateTile
            name="Universe"
            ariaValue={`${total} ${total === 1 ? "fact" : "facts"}${newCount > 0 ? `, ${newCount} new` : ""}`}
            onOpen={onOpenUniverse}
            dim={total === 0}
          >
            <span style={TILE_ROW}>
              <span style={TILE_NUM}>{total}</span>
              <span style={TILE_UNIT}>{total === 1 ? "fact" : "facts"}</span>
              {newCount > 0 ? <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--co-accent-ink)" }}>+{newCount}</span> : null}
            </span>
            <span style={TILE_SUB}>{newCount > 0 ? `${newCount} new since ${sinceLabel}` : "no new facts this week"}</span>
          </StateTile>

          {/* Signals: N live (active) / M new (pending). */}
          <StateTile
            name="Signals"
            ariaValue={`${activeSignals.length} live, ${pendingSignals.length} new`}
            onOpen={onOpenSignals}
            loading={signalsLoading}
            error={signalsError}
            dim={!signalsError && activeSignals.length === 0 && pendingSignals.length === 0}
          >
            <span style={TILE_ROW}>
              <span style={TILE_NUM}>{activeSignals.length}</span>
              <span style={TILE_UNIT}>live</span>
              <span style={{ color: "var(--co-faint)" }}>/</span>
              <span style={{ ...TILE_NUM, fontSize: "clamp(20px,2vw,26px)" }}>{pendingSignals.length}</span>
              <span style={TILE_UNIT}>new</span>
            </span>
          </StateTile>

          {/* Calendar: coverage framing only (slots FILLED vs total). There IS a
              publish pipeline behind this. SlotDeliverableSummary carries
              publications[] with DRAFT | READY | SCHEDULED | PUBLISHING |
              PUBLISHED | FAILED | CANCELLED plus a permalink. What genuinely does
              not exist is a publish TIMESTAMP (publishedAt is selected out of the
              month read) and anything worded as a "recommendation"
              (CalendarRecommendedSlot was deleted in v2). So "published" is
              sayable here, "published at 14:20" is not. */}
          <StateTile
            name="Calendar"
            ariaValue={calendar.slots.length === 0
              ? "no calendar yet"
              : `${filledSlots} of ${calendar.slots.length} slots filled${missedSlots > 0 ? `, ${missedSlots} missed` : ""}`}
            onOpen={onOpenCalendar}
            loading={calendar.isLoading}
            error={calendar.isError}
            dim={calendar.slots.length === 0}
          >
            {calendar.slots.length === 0 ? (
              <span style={{ ...TILE_UNIT, fontSize: 15 }}>no calendar yet</span>
            ) : (
              <>
                <span style={TILE_ROW}>
                  <span style={TILE_NUM}>{filledSlots}<span style={{ color: "var(--co-faint)", fontWeight: 600 }}>/{calendar.slots.length}</span></span>
                  <span style={TILE_UNIT}>filled</span>
                </span>
                <span style={TILE_SUB}>
                  {/* MISSED leads when present: it is the only calendar state
                      that needs a person, and it was invisible here before. */}
                  {missedSlots > 0
                    ? `${missedSlots} missed${openSlots.length > 0 ? ` · ${openSlots.length} open` : ""}`
                    : openSlots.length > 0
                      ? `${openSlots.length} open · next gap ${shortDate(openSlots[0].scheduledAt)}`
                      : "no gaps"}
                </span>
              </>
            )}
          </StateTile>
        </div>
      </section>

      {/* The band's OWN flags, not the calendar's combined ones: every row here
          comes off the plans LIST, and gating it on the current month's DETAIL
          read left it on skeletons while its data sat ready. */}
      <MonthPipeline
        months={calendar.months}
        planned={calendar.slots.length}
        published={publishedSlots}
        loading={calendar.monthsLoading}
        error={calendar.monthsError}
        onOpenCalendar={onOpenCalendar}
      />

      <div className="co-vl-regions" style={{ marginTop: 34 }}>
        <section aria-label="What's happening" style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          <RegionHeader label="WHAT'S HAPPENING" onOpen={onOpenUniverse} openLabel="open the universe" />

          {/* Agent-liveness heartbeat: one curated sentence, never a raw run log. */}
          {heartbeat ? (
            <div style={{ display: "flex", alignItems: "center", gap: 9, margin: "12px 2px 0" }}>
              {/* The dot reports health, and health is a reading taken off runs.
                  A brand with no runs gets the sentence alone: a live green dot
                  beside "have not run yet" would be the page contradicting
                  itself in the same line. */}
              {heartbeat.everRan ? (
                <span
                  className={agents.running.length ? "co-livedot" : undefined}
                  style={{ width: 7, height: 7, borderRadius: "50%", background: agents.allHealthy ? "var(--co-live)" : "var(--co-alert)", boxShadow: agents.allHealthy ? "0 0 8px rgba(34,197,94,.6)" : "none", flex: "none" }}
                />
              ) : null}
              <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--co-muted)" }}>{heartbeat.text}</span>
            </div>
          ) : null}

          {/* A failed agent-runs query must never read as "no agents ran". */}
          {agents.isError ? <ErrorNote label="Could not load agent runs" /> : null}

          <MaturityPanel maturity={maturity} onOpenUniverse={onOpenUniverse} />

          {/* The facts that grew, called out by concept + learnedAt. */}
          <div style={{ marginTop: 18, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--co-faint)" }}>
              {freshFacts.length ? `${freshFacts.length} ${freshFacts.length === 1 ? "fact" : "facts"} grew since ${sinceLabel}` : "No new facts this week"}
            </div>
            {freshFacts.length ? (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                {freshFacts.slice(0, 5).map((f) => {
                  const lit = seam.factId === f.id;
                  const enter = () => setSeam({ factId: f.id, callId: callByFactId.get(f.id) ?? null });
                  return (
                    <div
                      key={f.id}
                      className="co-vl-fresh"
                      data-lit={lit}
                      tabIndex={0}
                      aria-label={`${f.concept} fact: ${f.label}, learned ${shortDate(f.learnedAt)}`}
                      onMouseEnter={enter}
                      onMouseLeave={() => setSeam(NO_SEAM)}
                      onFocus={enter}
                      onBlur={() => setSeam(NO_SEAM)}
                      style={{ display: "flex", alignItems: "baseline", gap: 11, padding: "9px 8px", borderTop: "1px solid var(--co-line)" }}
                    >
                      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--co-accent)", boxShadow: "0 0 8px rgba(var(--co-accent-rgb),.7)", flex: "none", alignSelf: "center" }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--co-muted)" }}>{f.concept}</div>
                        <div style={{ fontFamily: "var(--font-section)", fontWeight: 700, fontSize: 14.5, lineHeight: 1.2, letterSpacing: "-.005em", color: "var(--co-text)", marginTop: 2 }}>{f.label}</div>
                      </div>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--co-faint)", flex: "none" }}>{shortDate(f.learnedAt)}</span>
                    </div>
                  );
                })}
                {freshFacts.length > 5 ? (
                  <button
                    className="co-vl-link"
                    onClick={() => onOpenUniverse?.()}
                    style={{ alignSelf: "flex-start", marginTop: 8, display: "inline-flex", alignItems: "center", background: "none", border: "none", padding: "4px 2px", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 11.5, letterSpacing: ".06em", color: "var(--co-muted)" }}
                  >
                    + {freshFacts.length - 5} more in the Universe →
                  </button>
                ) : null}
              </div>
            ) : agentsEverRan ? (
              <p style={{ margin: "10px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "var(--co-muted)" }}>
                Your agents are watching. The universe is holding steady this week.
              </p>
            ) : null
            /* Agents that have never run are not watching, and "no new facts
               this week" above is already the whole of what is known. The
               heartbeat at the top of this region says the rest, once. */
            }
          </div>

        </section>

        <section aria-label="What needs me" style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          <RegionHeader
            label="WHAT NEEDS ME"
            sub={ranked.length ? "pending, strongest first" : undefined}
            onOpen={onOpenSignals}
            openLabel="open signals"
          />

          <DecisionLayer rows={decisionRows} />

          {firstEver ? (
            <FirstEverCall onOpenSignals={onOpenSignals} />
          ) : shownSignals.length === 0 ? (
            // Empty list vs failed query: show the error note instead of the quiet empty state.
            signalsError ? (
              <div style={{ marginTop: 12 }}>
                <ErrorNote label="Could not load signals" />
              </div>
            ) : (
              <NothingWaiting agentsEverRan={agentsEverRan} />
            )
          ) : (
            <section aria-labelledby="co-vl-calls-label" style={{ marginTop: 18, minWidth: 0 }}>
              {/* Named so the two kinds of call do not blur: the thin rows above
                  are queues you open, these are decisions you take in place. */}
              <h3 id="co-vl-calls-label" style={SUB_EYEBROW}>SIGNALS WAITING</h3>
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                {shownSignals.map((sig) => (
                  <CallCard
                    key={sig.id}
                    signal={sig}
                    fact={groundFact(sig)}
                    decision={decisions[sig.id]}
                    busy={!!submitting[sig.id]}
                    lit={seam.callId === sig.id}
                    onSeam={setSeam}
                    onDecide={decideSignal}
                    onOpen={onOpenSignals}
                    onOpenUniverse={onOpenUniverse}
                    canEditOverride={preview?.canEdit}
                  />
                ))}
                {morePending > 0 ? (
                  <button
                    className="co-vl-link"
                    onClick={() => onOpenSignals?.()}
                    style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", background: "none", border: "none", padding: "4px 2px", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 11.5, letterSpacing: ".06em", color: "var(--co-muted)" }}
                  >
                    + {morePending} more waiting in Signals →
                  </button>
                ) : null}
              </div>
            </section>
          )}
        </section>
      </div>

      {/* Both social reads are threaded in, because they answer at different
          times: the connections endpoint answers first and the stats endpoint
          aggregates posts and profile snapshots behind it. Passing only the
          connection phase made every row build with `stats: null` on the normal
          first paint, which printed "Connected, not synced yet." over accounts
          that had synced minutes earlier. */}
      <LandingBand
        rows={landingRows}
        statsLoading={socialStats.isLoading}
        statsError={socialStats.isError}
        loading={socialConnections.isLoading}
        error={socialConnections.isError}
      />
    </div>
  );
}

// -------------------------------------------------------------- the month work

/** How many month rows the band shows before it hands off to the calendar. */
const MONTH_ROW_CAP = 4;

/**
 * Which months the band shows: newest first, capped, and the CURRENT month is
 * ALWAYS among them.
 *
 * A plain `slice` loses it. The auto-planner can run several months ahead, and
 * the moment there are four months newer than this one, the only row entitled to
 * a publish read drops behind "+N more months in the calendar" and the band
 * reports nothing but the future.
 *
 * When the current month falls outside the cap it takes the OLDEST shown row's
 * place, so the cap is never exceeded and the list stays newest first: every
 * month kept above it is newer than it by definition.
 */
function visibleMonths(months: MonthPipelineRow[], cap: number): MonthPipelineRow[] {
  const head = months.slice(0, cap);
  if (head.some((m) => m.isCurrent)) return head;
  const current = months.find((m) => m.isCurrent);
  if (!current) return head;
  return [...months.slice(0, cap - 1), current];
}

/**
 * The four month states, in the operator's words. Every legacy status was
 * folded onto APPROVED upstream (see monthLiveStatus), so there are four states
 * here and never thirteen.
 */
const MONTH_STATE_COPY: Record<MonthLiveStatus, string> = {
  DRAFT: "not started",
  GENERATING: "generating",
  APPROVED: "live",
  ARCHIVED: "done",
};

/**
 * THE WORK: which months are in flight, and where each one is stuck.
 *
 * The object of this band is the MONTH. The question it answers is "which
 * months are moving and which one is stuck on me", which is not a question a
 * day grid can answer: a grid of 31 cells reports what happened on the 14th,
 * and nobody asked that.
 *
 * ONE row is allowed to draw the eye, and it is the month whose planning FAILED
 * — the only row that is a report rather than a status. It is marked by its
 * wording first ("generation failed"), with an alert dot and the darker ink as
 * second and third cues, so the state never rides on colour alone. No month
 * waits on a person here: months approve themselves the moment they are
 * planned, and what a person still owes is owed post by post.
 *
 * THE HONESTY LINE. `useCalendar` detail-loads exactly one plan, the current
 * month, so the current month's row is the ONLY row that may carry a fill count
 * or a published count. Every other row has the list read's answer and nothing
 * else: its state and the size it was planned for. Enriching the others is one
 * call each, and until that call is made this band does not pretend otherwise.
 */
function MonthPipeline({
  months,
  planned,
  published,
  loading,
  error,
  onOpenCalendar,
}: {
  months: MonthPipelineRow[];
  /** Current-month slots that exist. Read for the current row only. */
  planned: number;
  /** Current-month slots with a publication that reached PUBLISHED. */
  published: number;
  /** The PLANS LIST's phase, never the current month's detail read: every row
      below is list data, and the band answers when the list does. */
  loading: boolean;
  error: boolean;
  onOpenCalendar?: () => void;
}) {
  const shown = visibleMonths(months, MONTH_ROW_CAP);
  const more = months.length - shown.length;

  let body: ReactNode;
  if (loading) {
    body = (
      <div aria-hidden="true" style={{ marginTop: 10 }}>
        {/* The cap, not a smaller number of its own. The band can never settle
            longer than this, so the read landing can only take rows away and
            never push what is below the band down under the reader. */}
        {Array.from({ length: MONTH_ROW_CAP }, (_, i) => (
          <div key={i} className="co-vl-mrow">
            {/* Two bars, not one, and the top one tracks the same clamp the month
                name is set in: a row standing in for a two-line row has to be
                about as tall as one, or the band still moves when the read
                lands, just by less. */}
            <div style={{ minWidth: 0 }}>
              <span style={{ display: "block", width: "38%", height: "clamp(22px,2.2vw,29.7px)", background: "var(--co-line)" }} />
              <span style={{ display: "block", marginTop: 12, width: "22%", height: 16, background: "var(--co-line)" }} />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = <ErrorNote label="Could not load your months" />;
  } else if (!shown.length) {
    // Not the same thing as an empty month: no plan row means nobody has
    // planned one yet, and a list of zero months must never read as a month
    // that came back empty.
    body = (
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13.5, color: "var(--co-muted)" }}>No months planned yet.</span>
        <button className="co-vl-link" onClick={() => onOpenCalendar?.()} style={LINK_STYLE}>
          open the calendar →
        </button>
      </div>
    );
  } else {
    body = (
      <>
        {/* Named, and the months inside it are real headings. Four rows of 27px
            display type in plain divs carry no heading level at all, so a screen
            reader has no way to move month to month. */}
        <ol className="co-vl-months" aria-label="Months, newest first" style={{ marginTop: 10 }}>
          {shown.map((row) => (
            <MonthRow
              key={row.id}
              row={row}
              /* The current month is the only row with slots behind it, so it
                 is the only row that may read its publish reality. */
              detail={row.isCurrent ? currentMonthDetail(row, planned, published) : plannedSizeDetail(row)}
              onOpenCalendar={onOpenCalendar}
            />
          ))}
        </ol>
        {more > 0 ? (
          <button
            className="co-vl-link"
            onClick={() => onOpenCalendar?.()}
            style={{ ...LINK_STYLE, marginTop: 10 }}
          >
            + {more} more {more === 1 ? "month" : "months"} in the calendar →
          </button>
        ) : null}
      </>
    );
  }

  return (
    <section
      className="co-vl-work"
      aria-labelledby="co-vl-work-label"
      aria-busy={loading || undefined}
      style={{ marginTop: 34, paddingBottom: 34, borderBottom: "1px solid var(--co-line)" }}
    >
      {/* No action here: every row below carries its own way in, and a fifth
          identical link above them is noise. */}
      <BandHeader id="co-vl-work-label" label="THE WORK" sub="newest first" />
      {body}
    </section>
  );
}

/**
 * How big a month was planned for. The plans list answers this for every month,
 * and for a month with no slots in hand it is the whole of what may be said.
 */
function plannedSizeDetail(row: MonthPipelineRow): string | null {
  return row.targetSlotCount > 0 ? `${row.targetSlotCount}-post month` : null;
}

/**
 * The current month's detail line, and the only row on the band that reads its
 * PUBLISH reality: `useCalendar` detail-loads exactly one plan.
 *
 * It does NOT repeat the fill count. The CALENDAR tile above already prints
 * "7/13 filled", and printing the same number twice on one screen spends the
 * row's line on the tile's job. The tile carries the count; the row carries the
 * state, and its detail is what happened to the month, not how full it is.
 *
 * "published" counts posts with a publication that reached PUBLISHED. There is
 * no publish timestamp anywhere in the month read, so nothing here may say when.
 */
function currentMonthDetail(row: MonthPipelineRow, planned: number, published: number): string | null {
  /* No slots in hand: a current month still in DRAFT or GENERATING, or the
     window before its own detail read lands. It falls back to the same size
     phrasing every other row carries rather than being the one row on the band
     with nothing under its name. */
  if (planned === 0) return plannedSizeDetail(row);
  return published > 0 ? `${published} published` : "nothing published yet";
}

/** One month: the month itself, where it is, and a way in. */
function MonthRow({
  row,
  detail,
  onOpenCalendar,
}: {
  row: MonthPipelineRow;
  detail: string | null;
  onOpenCalendar?: () => void;
}) {
  /* Four rows can share the accessible name "open the calendar". The month and
     its state are what tell them apart, so the button points at them. */
  const nameId = useId();
  // The one row that is a REPORT rather than a status, and so the only one
  // allowed the emphasis.
  const failed = row.generationError !== null;
  const state = failed ? "generation failed" : MONTH_STATE_COPY[row.status];

  return (
    <li className="co-vl-mrow">
      <div id={nameId} style={{ minWidth: 0 }}>
        {/* h3, under the band's "THE WORK" h2. Carries its own resets for the
            same reason SUB_EYEBROW does: the global heading rules would land
            40px display type on every month. */}
        <h3
          style={{
            margin: 0,
            fontFamily: "var(--font-section)",
            fontWeight: 800,
            fontSize: "clamp(20px,2vw,27px)",
            lineHeight: 1.1,
            letterSpacing: "-.012em",
            color: "var(--co-text)",
          }}
        >
          {row.label}
        </h3>
        <div style={{ marginTop: 7, display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
          {/* A small dot, never a fill: the state is carried by the sentence,
              and the dot is the second cue on top of it. */}
          {failed ? (
            <span
              aria-hidden="true"
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "var(--co-alert)",
                flex: "none",
              }}
            />
          ) : null}
          <span
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 13.5,
              // --co-alert is ~3.66:1 on white, under the 4.5:1 floor for text
              // this size, so the sentence takes the darker ink and the dot
              // above keeps carrying the colour.
              color: failed ? "var(--co-alert-ink)" : "var(--co-muted)",
            }}
          >
            {state}
          </span>
          {detail ? (
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".02em", color: "var(--co-faint)" }}>
              {detail}
            </span>
          ) : null}
        </div>
      </div>
      <button
        className="co-vl-link"
        aria-describedby={nameId}
        onClick={() => onOpenCalendar?.()}
        style={{ ...LINK_STYLE, flex: "none" }}
      >
        open the calendar →
      </button>
    </li>
  );
}

// ------------------------------------------------------------ universe maturity

/**
 * What Region A was missing. The bar is the brand's progress toward the
 * total-fact target, the rows are the three thinnest orbits, and `offRing` is
 * stated in words rather than folded into a concept: the backend supports
 * conceptless facts on purpose, and hiding them is what made the meter
 * overcount.
 */
function MaturityPanel({
  maturity,
  onOpenUniverse,
}: {
  maturity: ReturnType<typeof computeUniverseMaturity>;
  onOpenUniverse?: () => void;
}) {
  const pct = Math.round(maturity.progress * 100);
  const thinnest = maturity.nextUp.slice(0, 3);
  return (
    <section aria-labelledby="co-vl-maturity-label" style={{ marginTop: 18, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <h3 id="co-vl-maturity-label" style={SUB_EYEBROW}>UNIVERSE MATURITY</h3>
        <button className="co-vl-link" onClick={() => onOpenUniverse?.()} style={LINK_STYLE}>
          see the orbits →
        </button>
      </div>

      <div style={{ marginTop: 8, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "var(--font-section)", fontWeight: 700, fontSize: 13, letterSpacing: "-.01em", color: "var(--co-text)" }}>
          {maturityLabel(maturity)}
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--co-faint)" }}>{pct}%</span>
      </div>

      {/* No width transition: the house motion floor animates opacity, transform
          and colour only, and a progress width tween is a layout animation. */}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={`Universe maturity, ${maturityLabel(maturity)}`}
        style={{ marginTop: 8, height: 3, width: "100%", background: "var(--co-constant-soft)", overflow: "hidden" }}
      >
        <div style={{ height: "100%", width: `${pct}%`, background: maturity.tier === "mature" ? "var(--co-live)" : "var(--co-accent)" }} />
      </div>

      {thinnest.length ? (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
          {thinnest.map((c) => (
            <div
              key={c.ring}
              style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "7px 2px", borderTop: "1px solid var(--co-line)" }}
            >
              <span style={{ fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--co-muted)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.label}
              </span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: c.count > 0 ? "var(--co-text)" : "var(--co-faint)", fontWeight: 600, flex: "none" }}>
                {c.count}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {maturity.offRing > 0 ? (
        <p style={{ margin: "10px 0 0", fontSize: 12, lineHeight: 1.5, color: "var(--co-muted)" }}>
          {maturity.offRing} {maturity.offRing === 1 ? "fact sits" : "facts sit"} in no orbit yet.
        </p>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------- decision layer

/**
 * THE DECISION LAYER: up to four queues, each a thin row rather than a card, so
 * a queue you open never blurs into a call you decide in place.
 *
 * Three separate queries feed it (the calendar, the pillars read, the fact
 * proposal queue) and each row answers for its own: a failed proposals read must
 * not take the calendar rows down with it.
 */
/**
 * What the three reads add up to. `settled` counts only rows built from an
 * ANSWER: a skeleton is not a queue and an error is not a queue, and counting
 * them is what made the signal-card count shift under the reader (three
 * skeletons forced three cards, then one real row flipped the branch to four).
 */
type DecisionRows = {
  nodes: ReactNode[];
  /** Real, answered queues currently waiting on the operator. */
  settled: number;
  /** At least one of the three reads is still in flight. */
  loading: boolean;
};

function buildDecisionRows({
  calendar,
  plannable,
  factProposals,
  onOpenCalendar,
  onOpenStrategy,
  onOpenUniverse,
}: {
  calendar: ReturnType<typeof useCalendar>;
  plannable: ReturnType<typeof useBrandPlannable>;
  factProposals: ReturnType<typeof useFactProposals>;
  onOpenCalendar?: () => void;
  onOpenStrategy?: () => void;
  onOpenUniverse?: () => void;
}): DecisionRows {
  const rows: ReactNode[] = [];
  let settled = 0;
  const loading = calendar.isLoading || plannable.isLoading || factProposals.isLoading;

  // 1 + 3 share the calendar read, so they report ONE failure between them.
  if (calendar.isLoading) {
    rows.push(<RowSkeleton key="calendar-loading" />);
  } else if (calendar.isError) {
    rows.push(<RowError key="calendar-error" label="Could not load the month" />);
  } else {
    if (calendar.awaitingClient > 0) {
      // Never a person. There is no assignee anywhere in the calendar schema and
      // the post-to-person edge cannot be derived, so "your client" is the most
      // this row is allowed to say.
      settled++;
      rows.push(
        <DecisionRow
          key="client"
          text={`${calendar.awaitingClient} ${calendar.awaitingClient === 1 ? "post is" : "posts are"} with your client.`}
          actionLabel="open the calendar"
          onAction={onOpenCalendar}
        />,
      );
    }
  }

  // 2. A fact about the BRAND, never about a month: there is no "blocked"
  // status anywhere in the calendar schema. `reason` is non-null only when the
  // call answered and the answer was a null strategyId. Months plan fine
  // without one — this is what those months are missing, not a refusal.
  if (plannable.isLoading) {
    rows.push(<RowSkeleton key="plannable-loading" />);
  } else if (plannable.isError) {
    rows.push(<RowError key="plannable-error" label="Could not check whether this brand has a strategy" />);
  } else if (plannable.reason === "no-strategy") {
    settled++;
    rows.push(
      <DecisionRow
        key="plannable"
        text="This brand has no strategy, so its months are planned without pillars."
        actionLabel="open the strategy"
        onAction={onOpenStrategy}
      />,
    );
  }

  // 4. A genuinely different queue from the signals below: accepting a signal
  // sets it ACTIVE, accepting one of these writes a BrandUniverseFact.
  if (factProposals.isLoading) {
    rows.push(<RowSkeleton key="proposals-loading" />);
  } else if (factProposals.isError) {
    rows.push(<RowError key="proposals-error" label="Could not load the fact queue" />);
  } else if (factProposals.proposals.length > 0) {
    const n = factProposals.proposals.length;
    settled++;
    rows.push(
      <DecisionRow
        key="proposals"
        text={`${n} ${n === 1 ? "fact is" : "facts are"} waiting to join the universe.`}
        actionLabel="open the universe"
        onAction={onOpenUniverse}
      />,
    );
  }

  return { nodes: rows, settled, loading };
}

function DecisionLayer({ rows }: { rows: DecisionRows }) {
  return (
    /* Named, and the container points at the name: these thin rows and the
       decide-in-place cards below them must not blur together, and the two
       labels are the only thing separating them. */
    <section aria-labelledby="co-vl-decisions-label" aria-busy={rows.loading || undefined} style={{ marginTop: 16, minWidth: 0 }}>
      <h3 id="co-vl-decisions-label" style={SUB_EYEBROW}>OPEN DECISIONS</h3>
      {/* The skeletons are aria-hidden, so without this line the in-flight
          window is indistinguishable from the calm answered state: silence
          under the heading would read as "nothing is waiting" at the exact
          moment the app does not yet know. */}
      {rows.loading ? <span className="co-sr-only">Reading your queues</span> : null}
      {rows.nodes.length ? (
        <div style={{ marginTop: 6 }}>{rows.nodes}</div>
      ) : (
        // The block never disappears, and it never invents a fifth row to fill
        // itself. When all three reads answered and every queue is clear, this
        // one calm line is the honest whole of it, and it is scoped to THESE
        // three queues, because a standing line above may be counting waiting
        // calls and the signal cards below may be sitting undecided.
        <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: "var(--co-muted)" }}>
          Nothing in these queues.
        </p>
      )}
    </section>
  );
}

/** One queue: a sentence carrying its own real count, and a way in. */
function DecisionRow({
  text,
  extra,
  actionLabel,
  onAction,
}: {
  text: string;
  extra?: string;
  actionLabel: string;
  onAction?: () => void;
}) {
  /* Three of these rows can share the accessible name "open the calendar". The
     sentence beside each one is what tells them apart, so the button points at
     it and a reader pulling the buttons out of context still gets the queue. */
  const sentenceId = useId();
  return (
    <div className="co-vl-drow">
      <span id={sentenceId} style={{ fontSize: 13, lineHeight: 1.45, color: "var(--co-text)", minWidth: 0 }}>
        {text}
        {extra ? <span style={{ color: "var(--co-muted)" }}> {extra}</span> : null}
      </span>
      <button className="co-vl-link" aria-describedby={sentenceId} onClick={() => onAction?.()} style={{ ...LINK_STYLE, flex: "none" }}>
        {actionLabel} →
      </button>
    </div>
  );
}

/** A queue still being read. It says nothing about what it will find. */
function RowSkeleton() {
  return (
    <div className="co-vl-drow" aria-hidden="true">
      <span style={{ display: "block", width: "58%", height: 12, background: "var(--co-line)" }} />
    </div>
  );
}

/** A queue that could not be read. Never a zero: a zero here would be a claim. */
function RowError({ label }: { label: string }) {
  return (
    <div className="co-vl-drow" role="alert">
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {/* Dot on --co-alert (its floor is 3:1 and this is the sanctioned use),
            sentence on --co-alert-ink: below 18px the text needs 4.5:1. */}
        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--co-alert)", flex: "none" }} />
        <span style={{ fontSize: 12.5, lineHeight: 1.45, color: "var(--co-alert-ink)" }}>{label}. Try again in a moment.</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------- how it's landing

/**
 * HOW IT'S LANDING: point-in-time reach and engagement, and nothing else.
 *
 * There is NO history table behind these numbers. Both metric writes are Prisma
 * upserts, so the database holds one value per account and there is no prior
 * value anywhere to subtract from. That makes a rising line, a sparkline, a
 * delta, a "+X this week" or a growth percentage impossible to compute and
 * therefore impossible to draw honestly. The value and its sync time are the
 * whole of what may appear here.
 *
 * `engagementRate` arrives already multiplied out: the backend computes
 * (avgEngagement / followerCount) * 100 and rounds to two decimals, so it is
 * printed as-is with a percent sign and never scaled again.
 */
function LandingBand({
  rows,
  statsLoading,
  statsError,
  loading,
  error,
}: {
  rows: LandingRow[];
  /** The STATS query is still in flight. The connections query answers first,
      so this window is the normal first paint and not an edge case: until it
      closes no row may say anything about whether an account has synced. */
  statsLoading: boolean;
  statsError: boolean;
  loading: boolean;
  error: boolean;
}) {
  let body: ReactNode;
  if (loading) {
    body = (
      <div aria-hidden="true" style={{ marginTop: 14, display: "flex", flexDirection: "column" }}>
        {[0, 1].map((i) => (
          <div key={i} className="co-vl-landing-row">
            <span style={{ display: "block", width: "40%", height: 13, background: "var(--co-line)" }} />
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = <ErrorNote label="Could not load your connected accounts" />;
  } else if (!rows.length) {
    body = (
      <p style={{ margin: "14px 0 0", fontSize: 13, lineHeight: 1.5, color: "var(--co-muted)" }}>
        No accounts connected yet.
      </p>
    );
  } else {
    body = (
      <div style={{ marginTop: 8 }}>
        {rows.map((row) => (
          <LandingRowView key={row.accountId} row={row} statsLoading={statsLoading} statsError={statsError} />
        ))}
      </div>
    );
  }

  return (
    <section
      aria-labelledby="co-vl-landing-label"
      aria-busy={loading || statsLoading || undefined}
      style={{ marginTop: 34, paddingTop: 34, borderTop: "1px solid var(--co-line)" }}
    >
      <BandHeader
        id="co-vl-landing-label"
        label="HOW IT'S LANDING"
        sub="point in time, there is no history behind these numbers"
      />
      {body}
    </section>
  );
}

function LandingRowView({ row, statsLoading, statsError }: { row: LandingRow; statsLoading: boolean; statsError: boolean }) {
  const stats = row.stats;
  const synced = stats?.lastSyncedAt ?? null;
  const followers = stats ? numberOr(stats.followerCount) : null;
  const posts = stats ? numberOr(stats.postCount) : null;
  const rate = stats && stats.engagementRate != null ? `${stats.engagementRate}%` : null;

  const identity = (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
        <span style={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 13, color: "var(--co-text)" }}>
          {PROVIDER_LABEL[row.provider]}
        </span>
        {/* An account with neither a username nor a name renders no handle at
            all. There is nothing else on the row to derive one from. */}
        {row.handle ? (
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--co-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            @{row.handle}
          </span>
        ) : null}
      </div>
      {synced ? (
        <div style={{ marginTop: 3, fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".02em", color: "var(--co-faint)" }}>
          as of {ago(synced)}
        </div>
      ) : null}
      {row.needsReauth ? (
        <div style={{ marginTop: 5, display: "inline-flex", alignItems: "center", gap: 7 }}>
          {/* The dot keeps carrying the state (3:1 is its floor), and the
              sentence takes the darker ink: --co-alert is ~3.66:1 on white,
              under the 4.5:1 floor for text this size. */}
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--co-alert)", flex: "none" }} />
          <span style={{ fontSize: 12, color: "var(--co-alert-ink)" }}>
            Needs reconnecting, so these numbers stopped updating.
          </span>
        </div>
      ) : null}
    </div>
  );

  /* Four distinct states, kept distinct. The stats query still being READ is
     not "not synced yet", "not synced yet" is not zero followers, and a failed
     stats query is none of the three. The in-flight window comes first because
     until it closes there is no answer to read. */
  if (statsLoading) {
    return (
      <div className="co-vl-landing-row">
        {identity}
        <Metric label="followers" value={null} loading />
        <Metric label="engagement rate" value={null} loading />
        <Metric label="posts" value={null} loading />
      </div>
    );
  }
  if (statsError) {
    return (
      <div className="co-vl-landing-row" data-solo="true">
        {identity}
        <span role="alert" style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--co-alert)", flex: "none" }} />
          <span style={{ fontSize: 12, color: "var(--co-alert-ink)" }}>couldn't load the numbers</span>
        </span>
      </div>
    );
  }
  if (!synced) {
    return (
      <div className="co-vl-landing-row" data-solo="true">
        {identity}
        <span style={{ fontSize: 12.5, color: "var(--co-muted)" }}>Connected, not synced yet.</span>
      </div>
    );
  }

  return (
    <div className="co-vl-landing-row">
      {identity}
      <Metric label="followers" value={followers} />
      <Metric label="engagement rate" value={rate} />
      <Metric label="posts" value={posts} />
    </div>
  );
}

/** One number, or an empty cell. A null metric is left blank, never zeroed, and
    a metric still being read is a skeleton rather than a blank (a blank cell is
    a claim that the number came back empty). */
function Metric({ label, value, loading }: { label: string; value: string | null; loading?: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      {loading ? (
        <span aria-hidden="true" style={{ display: "block", width: "62%", height: 14, margin: "2px 0", background: "var(--co-line)" }} />
      ) : (
        <div style={{ fontFamily: "var(--font-section)", fontWeight: 800, fontSize: 16, lineHeight: 1.1, letterSpacing: "-.01em", color: value ? "var(--co-text)" : "var(--co-faint)", minHeight: 18 }}>
          {value ?? ""}
        </div>
      )}
      <div style={{ marginTop: 3, fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".02em", color: "var(--co-faint)" }}>
        {label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- primitives

/** Full-width band header: the PRODUCT STATE eyebrow, plus a sub-label and a
    way out. SectionTag carries neither, and it would need a section index this
    page does not have. */
function BandHeader({
  id,
  label,
  sub,
  actionLabel,
  onAction,
}: {
  id?: string;
  label: string;
  sub?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, minWidth: 0, flexWrap: "wrap" }}>
        <h2 id={id} style={BAND_EYEBROW}>{label}</h2>
        {sub ? (
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".04em", color: "var(--co-faint)" }}>{sub}</span>
        ) : null}
      </div>
      {actionLabel && onAction ? (
        <button className="co-vl-link" onClick={() => onAction()} style={{ ...LINK_STYLE, flex: "none" }}>
          {actionLabel} →
        </button>
      ) : null}
    </div>
  );
}

/** Region header with optional sub-label and navigation link. */
function RegionHeader({ label, sub, onOpen, openLabel }: { label: string; sub?: string; onOpen?: () => void; openLabel: string }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        {/* h2 styled as mono eyebrow for nested h3 call cards. */}
        <h2 style={BAND_EYEBROW}>{label}</h2>
        <button
          className="co-vl-link"
          onClick={() => onOpen?.()}
          style={{ flex: "none", display: "inline-flex", alignItems: "center", background: "none", border: "none", padding: "2px 4px", cursor: "pointer", fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 11, letterSpacing: ".04em", color: "var(--co-info)" }}
        >
          {openLabel} →
        </button>
      </div>
      {sub ? <div style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".04em", color: "var(--co-faint)" }}>{sub}</div> : null}
    </div>
  );
}

/** Pending signal card with in-place accept/refine/decline actions. */
function CallCard({
  signal,
  fact,
  decision,
  busy,
  lit,
  onSeam,
  onDecide,
  onOpen,
  onOpenUniverse,
  canEditOverride,
}: {
  signal: MarketSignal;
  fact?: StageFact;
  decision?: Decision;
  busy: boolean;
  lit: boolean;
  onSeam: (s: Seam) => void;
  onDecide: (signal: MarketSignal, d: Decision) => void;
  onOpen?: () => void;
  /** Plain navigation, not a decision: nothing is recorded and the call stays
      pending until Accept or Decline is pressed. */
  onOpenUniverse?: () => void;
  /** Harness override; live mounts leave it undefined and use real permissions. */
  canEditOverride?: boolean;
}) {
  const { canEdit: livePermission } = usePermissions();
  const canEdit = canEditOverride ?? livePermission;
  const conf = signal.confidence != null ? Math.round(signal.confidence * 100) : null;
  const { supportCount, distinctSourceCount } = signalEvidenceStats(signal);
  const validLabel = approxDuration(signal.recommendedValidUntil ?? signal.validUntil);
  const light = () => onSeam({ factId: fact?.id ?? null, callId: signal.id });
  const dim = () => onSeam(NO_SEAM);
  // Keep the planet lit while focus moves between this card's own controls.
  const onBlur = (e: FocusEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    dim();
  };

  // Focus restoration (SC 2.4.3/2.4.7): deciding unmounts the buttons the user
  // was on. Focus moves to the card itself, which persists and whose role=status
  // confirmation announces the outcome, so focus never falls to <body>.
  const articleRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!decision) return;
    articleRef.current?.focus();
  }, [decision]);

  const meta: string[] = [kindLabelSingular(signal.kind)];
  if (fact?.concept) meta.push(`grounded in ${fact.concept.toLowerCase()}`);
  if (supportCount) meta.push(`${supportCount} ${supportCount === 1 ? "source" : "sources"}${distinctSourceCount > 1 ? ` · ${distinctSourceCount} distinct` : ""}`);
  if (conf != null) meta.push(`${conf}/100`);
  meta.push(validLabel);

  return (
    <article
      ref={articleRef}
      className="co-vl-call co-card"
      data-lit={lit}
      tabIndex={0}
      onMouseEnter={light}
      onMouseLeave={dim}
      onFocus={light}
      onBlur={onBlur}
      style={{ padding: "14px 16px", outlineOffset: 2 }}
    >
      {/* h4, not h3: these sit UNDER the "SIGNALS WAITING" h3, and the whole point
          of that label is that a decide-in-place card is a different kind of item
          from the thin queue rows above it. As siblings they read as the same thing. */}
      <h4 style={{ fontFamily: "var(--font-section)", fontWeight: 800, fontSize: "clamp(15px,1.4vw,18px)", lineHeight: 1.16, letterSpacing: "-.008em", color: "var(--co-text)", margin: 0 }}>
        {signal.title}
      </h4>
      <div style={{ margin: "8px 0 0", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".02em", color: "var(--co-muted)" }}>
        {meta.map((m, i) => (
          <span key={i}>
            {i > 0 ? <span style={{ color: "var(--co-faint)" }}> · </span> : null}
            {m}
          </span>
        ))}
      </div>

      <div style={{ marginTop: 12, minHeight: 32, display: "flex", alignItems: "center" }}>
        {decision ? (
          // No Undo: both decisions hit the server and neither has a reversal
          // endpoint, so an Undo here would offer something that cannot happen.
          <Decided decision={decision} />
        ) : (
          <div aria-busy={busy} style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", opacity: busy ? 0.6 : 1 }}>
            {canEdit ? (
              <>
                <button className="co-btn" disabled={busy} onClick={() => onDecide(signal, "accepted")} style={ACCEPT_STYLE}>Accept</button>
                {/* Labelled for what it does, which is open the universe, not
                    "Refine": that promised a capability which is not built. It
                    routes and records nothing, so the call stays pending. */}
                <button disabled={busy} onClick={() => onOpenUniverse?.()} style={GHOST_STYLE}>Open in Universe</button>
                <button disabled={busy} onClick={() => onDecide(signal, "declined")} style={QUIET_STYLE}>Decline</button>
              </>
            ) : null}
            <button className="co-vl-link" onClick={() => onOpen?.()} style={{ ...LINK_STYLE, marginLeft: "auto", opacity: 1 }}>see the evidence →</button>
          </div>
        )}
      </div>
    </article>
  );
}

/** The taken-decision confirmation. No Undo: accept and decline both hit the
    server and neither has a reversal endpoint. */
function Decided({ decision }: { decision: Decision }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <span role="status" aria-live="polite" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--co-text)", fontSize: 13 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--co-live)", boxShadow: "0 0 8px rgba(34,197,94,.7)", flex: "none" }} />
        {DECIDED_COPY[decision]}
      </span>
    </div>
  );
}

/** Inline error when a section's query failed. */
function ErrorNote({ label }: { label: string }) {
  return (
    <div role="alert" style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 2px 0" }}>
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--co-alert)", flex: "none" }} />
      <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--co-alert-ink)" }}>{label}. Try again in a moment.</span>
    </div>
  );
}

/** Empty state when there are no pending signal calls. Worded for THIS list:
    the decision layer above answers the wider "is anything waiting on me".

    Two readings, because an empty list has two causes. Agents that HAVE run and
    found nothing are holding steady, and the live dot is earned. Agents that
    have never run are not watching anything, so the card reports the absence in
    the future tense and drops the dot: the same over-claim the heartbeat above
    was fixed for would otherwise reappear here in different words. */
function NothingWaiting({ agentsEverRan }: { agentsEverRan: boolean }) {
  return (
    <div className="co-card" style={{ marginTop: 18, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 160, padding: "28px 24px", gap: 9 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {agentsEverRan ? (
          <span className="co-livedot" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--co-live)", boxShadow: "0 0 8px rgba(34,197,94,.6)", flex: "none" }} />
        ) : null}
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--co-faint)" }}>
          {agentsEverRan ? "HOLDING STEADY" : "NOTHING WAITING YET"}
        </span>
      </div>
      <h3 style={{ fontFamily: "var(--font-section)", fontWeight: 800, fontSize: "clamp(19px,1.9vw,25px)", lineHeight: 1.1, letterSpacing: "-.01em", color: "var(--co-text)", margin: 0 }}>
        No calls waiting.
      </h3>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "var(--co-muted)" }}>
        {agentsEverRan
          ? "Your agents are watching. The moment something crosses the bar, it lands here, ranked."
          : "The moment your agents start watching, the week's calls will be ranked and waiting here."}
      </p>
    </div>
  );
}

/** Empty state for a brand with no data yet. */
function FirstEverCall({ onOpenSignals }: { onOpenSignals?: () => void }) {
  return (
    <div className="co-card" style={{ marginTop: 18, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 160, padding: "28px 24px", gap: 12 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--co-faint)" }}>NOTHING WAITING YET</div>
      <h3 style={{ fontFamily: "var(--font-section)", fontWeight: 800, fontSize: "clamp(19px,1.9vw,25px)", lineHeight: 1.1, letterSpacing: "-.01em", color: "var(--co-text)", margin: 0, maxWidth: 420 }}>
        No calls waiting yet.
      </h3>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "var(--co-muted)", maxWidth: 420 }}>
        The moment your agents start watching, the week's calls will be ranked and waiting here.
      </p>
      {/* The label now matches where the button actually goes. It read "Connect
          your sources" while navigating to Signals, which is not a
          source-connection surface. */}
      <button className="co-btn" onClick={() => onOpenSignals?.()} style={{ alignSelf: "flex-start", marginTop: 2, fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", padding: "11px 20px", cursor: "pointer" }}>
        Open Signals
      </button>
    </div>
  );
}

/** Status tile: skeleton while loading, inline error state on failure, dimmed at zero. */
function StateTile({
  name,
  ariaValue,
  onOpen,
  loading,
  error,
  announceError,
  dim,
  disabled,
  children,
}: {
  name: string;
  ariaValue: string;
  onOpen?: () => void;
  loading?: boolean;
  error?: boolean;
  // Announce this tile's error render as an alert. Set ONLY for tiles with no
  // region-level role="alert" mirroring them (Strategy). Signals/Calendar/Agents
  // are already announced by their regions, so they must NOT set this (it would
  // double-announce).
  announceError?: boolean;
  dim?: boolean;
  disabled?: boolean;
  children?: ReactNode;
}) {
  let body: ReactNode;
  if (loading) {
    body = <span aria-hidden="true" style={{ display: "block", width: "62%", height: 30, marginTop: 14, background: "var(--co-line)" }} />;
  } else if (error) {
    body = (
      <span {...(announceError ? { role: "alert" } : {})} style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 14 }}>
        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--co-alert)", flex: "none" }} />
        <span style={{ fontSize: 12, color: "var(--co-alert-ink)" }}>couldn't load</span>
      </span>
    );
  } else {
    body = <div style={{ marginTop: 14 }}>{children}</div>;
  }

  const inner = (
    <>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--co-faint)" }}>{name}</span>
      {body}
    </>
  );

  const base: CSSProperties = {
    display: "block",
    textAlign: "left",
    width: "100%",
    minWidth: 0,
    padding: "20px 20px 22px",
    opacity: dim && !loading && !error ? 0.55 : 1,
  };

  if (disabled) {
    return (
      <div className="co-card" aria-disabled="true" style={{ ...base, opacity: 0.5 }}>
        {inner}
      </div>
    );
  }
  if (onOpen) {
    const aria = loading ? `${name}: loading` : error ? `${name}: could not load, see the panels below` : `${name}: ${ariaValue}`;
    return (
      <button type="button" className="co-card co-vl-tile" onClick={() => onOpen()} aria-label={aria} style={{ ...base, cursor: "pointer", font: "inherit", color: "inherit" }}>
        {inner}
      </button>
    );
  }
  // Non-clickable fallback (route-out prop not wired): still a labelled data tile.
  return (
    <div className="co-card" role="group" aria-label={`${name}: ${ariaValue}`} style={base}>
      {inner}
    </div>
  );
}

/** The band eyebrow, shared by every full-width section and both regions. */
const BAND_EYEBROW: CSSProperties = { margin: 0, fontFamily: "var(--font-mono)", fontWeight: 400, fontSize: 11, lineHeight: 1.2, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--co-text)" };
/** The quieter eyebrow for a block INSIDE a region. Carries the same resets as
    BAND_EYEBROW because it is worn by h3s: the global heading rules would
    otherwise land 40px display type on all three. */
const SUB_EYEBROW: CSSProperties = { margin: 0, fontFamily: "var(--font-mono)", fontWeight: 400, fontSize: 11, lineHeight: 1.2, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--co-faint)" };

const TILE_ROW: CSSProperties = { display: "flex", alignItems: "baseline", gap: 9, minWidth: 0, flexWrap: "wrap" };
const TILE_NUM: CSSProperties = { fontFamily: "var(--font-section)", fontWeight: 800, fontSize: "clamp(30px,3.2vw,40px)", letterSpacing: "-.018em", color: "var(--co-text)", lineHeight: 1 };
const TILE_UNIT: CSSProperties = { fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13, color: "var(--co-muted)" };
const TILE_SUB: CSSProperties = { display: "block", marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".02em", color: "var(--co-faint)" };

const ACCEPT_STYLE: CSSProperties = { fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 11.5, letterSpacing: ".06em", textTransform: "uppercase", padding: "8px 15px", cursor: "pointer" };
const GHOST_STYLE: CSSProperties = { fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 11.5, letterSpacing: ".06em", textTransform: "uppercase", padding: "7px 13px", cursor: "pointer", background: "transparent", border: "1px solid var(--co-line2)", color: "var(--co-text)" };
const QUIET_STYLE: CSSProperties = { fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 11.5, letterSpacing: ".06em", textTransform: "uppercase", background: "none", border: "none", color: "var(--co-muted)", padding: "7px 5px", cursor: "pointer" };
const LINK_STYLE: CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".04em", background: "none", border: "none", color: "var(--co-info)", padding: "7px 2px", cursor: "pointer", display: "inline-flex", alignItems: "center" };

/** View-scoped layout styles for the home dashboard grid and hover highlights. */
function VantageStyle() {
  return (
    <style>{`
      .co-vl-regions {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 28px;
      }
      @media (max-width: 900px) {
        .co-vl-regions { grid-template-columns: minmax(0, 1fr); gap: 22px; }
      }
      /* Container query: tile band responds to content width, not viewport. */
      .co-vl-ps { container: co-ps / inline-size; }
      .co-vl-band {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
      }
      /* Uniform tile height so the three tiles read as one set, and tall enough
         that the numeral has room around it. min-height only grows, never clips. */
      .co-vl-band > * { min-height: 132px; }
      /* THREE tiles, and three divides cleanly: one column while the band is
         narrow, three the moment there is room for them. No spanning rule and
         no ragged last row at any width. */
      @container co-ps (min-width: 620px) {
        .co-vl-band { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      }
      .co-vl-tile {
        transition: border-color 0.15s var(--ease-out-expo), background 0.2s var(--ease-out-expo);
      }
      .co-vl-tile:hover { border-color: var(--co-accent); }
      @media (prefers-reduced-motion: reduce) {
        .co-vl-tile { transition: none; }
      }
      .co-vl-call {
        transition: border-color 0.2s var(--ease-out-expo), background 0.2s var(--ease-out-expo);
      }
      .co-vl-call[data-lit="true"] {
        border-color: var(--co-accent);
        background: var(--co-accent-dim);
      }
      .co-vl-fresh {
        transition: background 0.2s var(--ease-out-expo);
        cursor: default;
      }
      .co-vl-fresh[data-lit="true"] { background: var(--co-accent-dim); }
      .co-vl-fresh:focus-visible { outline: 2px solid var(--co-accent); outline-offset: -2px; }
      @media (prefers-reduced-motion: reduce) {
        .co-vl-call, .co-vl-fresh { transition: none; }
      }

      /* ---- THE WORK ---- */
      .co-vl-months {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .co-vl-mrow {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: 10px 18px;
        padding: 16px 2px;
        border-top: 1px solid var(--co-line);
      }
      /* The link drops under its month rather than squeezing the month name to
         a couple of words on a phone. */
      @media (max-width: 560px) {
        .co-vl-mrow {
          grid-template-columns: minmax(0, 1fr);
          justify-items: start;
        }
      }

      /* ---- decision rows ---- */
      .co-vl-drow {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        min-height: 40px;
        padding: 9px 2px;
        border-top: 1px solid var(--co-line);
      }

      /* ---- how it's landing ---- */
      .co-vl-landing-row {
        display: grid;
        grid-template-columns: minmax(0, 1.7fr) repeat(3, minmax(0, 1fr));
        align-items: center;
        gap: 14px;
        padding: 11px 2px;
        border-top: 1px solid var(--co-line);
      }
      /* A row with one status line instead of three numbers. */
      .co-vl-landing-row[data-solo="true"] {
        grid-template-columns: minmax(0, 1.7fr) minmax(0, 3fr);
      }
      @media (max-width: 700px) {
        .co-vl-landing-row,
        .co-vl-landing-row[data-solo="true"] {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 10px 14px;
        }
      }

      /* Touch targets. The floor lives here rather than in an inline style,
         because an inline min-height would out-specify the phone rule below and
         every link would stay a 24px sliver on a thumb. */
      .co-vl-link { min-height: 24px; }
      /* Keyed to the input as well as the width: an iPad in portrait is 768px
         wide, which sits outside the phone breakpoint, and it is still a thumb. */
      @media (max-width: 760px), (pointer: coarse) {
        .co-vl-link,
        .co-vl-call button,
        .co-vl-drow button { min-height: 44px; }
      }
    `}</style>
  );
}
